import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { Observable, distinctUntilChanged, filter, finalize, of, shareReplay, tap } from 'rxjs';
import { Workspace } from '@partiri-cloud/types';

import { ClientService } from '../api/client.service';
import { StorageService } from '../storage/storage.service';

/**
 * Single source of truth for "which workspace am I looking at".
 *
 * The id lives in memory as a signal and is mirrored to localStorage for
 * persistence across reloads — storage is the durable copy, never the
 * transport. Components must consume `current$` instead of reading storage in
 * `ngOnInit`: on a client-side login the id only exists once `/workspaces`
 * responds, long after the dashboard's children have initialised.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceService {
  private client = inject(ClientService);
  private storage = inject(StorageService);
  private platformId = inject(PLATFORM_ID);

  private readonly _id = signal<string | null>(this.storage.getItem('workspace'));
  private readonly _list = signal<Workspace[]>([]);
  private readonly _resolved = signal(false);

  /** Current workspace id, or null while unresolved. For imperative reads in handlers. */
  readonly id = this._id.asReadonly();
  /** Workspaces the user belongs to — populated by `load()`. */
  readonly list = this._list.asReadonly();
  /** True once a load attempt has settled, so consumers can tell "loading" from "none". */
  readonly resolved = this._resolved.asReadonly();
  /** True when resolution has settled and there is no workspace to work with. */
  readonly missing = computed(() => this._resolved() && this._id() === null);

  /**
   * Emits the current workspace id, now or as soon as it is known, and again on
   * every switch. Pair with `switchMap` so data reloads when the user switches.
   */
  readonly current$: Observable<string> = toObservable(this._id).pipe(
    filter((id): id is string => id !== null),
    distinctUntilChanged(),
  );

  /** Shared across concurrent callers so bootstrap and auth don't both fetch. */
  private inFlight: Observable<Workspace[]> | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Resolve as a consequence of authentication rather than of a component
    // mounting — this is what makes a client-side login land on a populated
    // dashboard instead of an empty one.
    this.client.supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        this.clear();
        return;
      }

      if (!session) {
        this._resolved.set(true);
        return;
      }

      // Already settled on a real workspace — a token refresh shouldn't refetch.
      if (this._resolved() && this._list().length > 0) return;

      this.fetch().subscribe({ error: () => undefined });
    });
  }

  /** Select a workspace and persist the choice. */
  setWorkspace(id: string): void {
    this.storage.setItem('workspace', id);
    this._id.set(id);
  }

  /** Forget the current workspace — used on sign out. */
  clear(): void {
    this.storage.removeItem('workspace');
    this._id.set(null);
    this._list.set([]);
    this._resolved.set(false);
  }

  /**
   * Resolve the user's workspaces, reusing an in-flight or already-settled
   * result. Errors are surfaced to the caller so the dashboard can report them.
   */
  load(): Observable<Workspace[]> {
    if (this._resolved() && this._list().length > 0) return of(this._list());
    if (!this.hasAuthToken()) {
      this._resolved.set(true);
      return of([]);
    }

    return this.fetch();
  }

  /**
   * Fetch the workspace list and settle on one. A stored id that is no longer
   * in the list is replaced with the first workspace, so a stale key from a
   * previous session can't produce a permanently empty dashboard.
   */
  private fetch(): Observable<Workspace[]> {
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.client.workspaces.list().pipe(
      tap({
        next: data => {
          this._list.set(data);
          this._resolved.set(true);

          if (data.length === 0) {
            // Drop the persisted id too, or the next reload seeds from a
            // workspace the user no longer belongs to.
            this.storage.removeItem('workspace');
            this._id.set(null);
            return;
          }

          const stored = this._id();
          if (stored === null || !data.some(w => w.id === stored)) {
            this.setWorkspace(data[0].id!);
          }
        },
        error: () => this._resolved.set(true),
      }),
      finalize(() => { this.inFlight = null; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight;
  }

  /**
   * Only used on the bootstrap path, where no Supabase session object is at
   * hand and awaiting one would block the app. ClientService writes this key
   * from its own auth listener.
   */
  private hasAuthToken(): boolean {
    return this.storage.keys().some(key => key.startsWith('sb-') && key.endsWith('-auth-token'));
  }
}
